import {Observable} from 'rxjs';
import {
    CpmCServiceRequestCreateApiSchema,
    CpmCServiceRequestUpdateApiSchema
} from '../../schemas/c-customer/cpmc-service-request.api-schemas';
import {CpmCApiBridge} from '../../../../../src/app/cpmc-api-bridge';
import {CpmCQueryPaginatorConfig, CpmCQueryPaginatorConfigUtils} from '../../cpmc-defines';
import {CpmCServiceRequestApiModel} from '../../models/c-customer/cpmc-service-request.api-model';
import {AllowedQueryParamTypes, QueryParams} from '../../../../../sdk/src/utils/query.utils';
import {CpmCPaginatedResultAPISchema} from '../../schemas/cpmc-paginated-result.api-schemas';

export type CpmCGetServiceRequestsParams = {
    [K in keyof CpmCServiceRequestApiModel]?: CpmCServiceRequestApiModel[K] extends AllowedQueryParamTypes
        ? CpmCServiceRequestApiModel[K]
        : never;
} & {} & QueryParams;

/**
 * ServiceRequest Api Service
 */
export class CpmCServiceRequestApiService {
    public static readonly dPaginatorConfig: CpmCQueryPaginatorConfig = {limit: -1, sort: 'id', order: 1};

    // ===============================================================
    // ===============================================================

    public static baseUrl(inCustomerUUID: string | undefined) {
        const base = CpmCApiBridge.getBackendBaseURL() + 'api/core/v1/';
        const customerUUID = CpmCApiBridge.getCurrentCustomerUUID();

        return base + `customer/${customerUUID || inCustomerUUID}/service-request`;
    }

    // ===============================================================
    // ===============================================================

    public static endpointList(inCustomerUUID: string | undefined): string {
        return `${CpmCServiceRequestApiService.baseUrl(inCustomerUUID)}/list`;
    }
    public static endpointNew(inCustomerUUID: string | undefined): string {
        return `${CpmCServiceRequestApiService.baseUrl(inCustomerUUID)}`;
    }
    public static endpointGet(inCustomerUUID: string | undefined, serviceRequestUUID: string): string {
        return `${CpmCServiceRequestApiService.baseUrl(inCustomerUUID)}/${serviceRequestUUID}`;
    }
    public static endpointEdit(inCustomerUUID: string | undefined, serviceRequestUUID: string): string {
        return `${CpmCServiceRequestApiService.baseUrl(inCustomerUUID)}/${serviceRequestUUID}`;
    }
    public static endpointDelete(inCustomerUUID: string | undefined, serviceRequestUUID: string): string {
        return `${CpmCServiceRequestApiService.baseUrl(inCustomerUUID)}/${serviceRequestUUID}`;
    }

    // ===============================================================
    // ===============================================================

    public static getServiceRequestList(
        params: CpmCGetServiceRequestsParams = {},
        paginatorConfig: CpmCQueryPaginatorConfig = CpmCServiceRequestApiService.dPaginatorConfig
    ): Observable<CpmCPaginatedResultAPISchema<CpmCServiceRequestApiModel>> {
        return CpmCApiBridge.httpGet<CpmCPaginatedResultAPISchema<CpmCServiceRequestApiModel>>(
            CpmCServiceRequestApiService.endpointList(undefined),
            {
                ...(params as QueryParams),
                ...CpmCQueryPaginatorConfigUtils.toQueryParams(paginatorConfig)
            }
        );
    }
    public static getServiceRequestByUUID(serviceRequestUUID: string): Observable<CpmCServiceRequestApiModel> {
        return CpmCApiBridge.httpGet<CpmCServiceRequestApiModel>(
            CpmCServiceRequestApiService.endpointGet(undefined, serviceRequestUUID)
        );
    }
    public static createServiceRequest(
        payload: CpmCServiceRequestCreateApiSchema
    ): Observable<CpmCServiceRequestApiModel> {
        return CpmCApiBridge.httpPost<CpmCServiceRequestApiModel>(
            CpmCServiceRequestApiService.endpointNew(undefined),
            payload
        );
    }
    public static updateServiceRequest(
        serviceRequestUUID: string,
        payload: CpmCServiceRequestUpdateApiSchema
    ): Observable<CpmCServiceRequestApiModel> {
        return CpmCApiBridge.httpPut<CpmCServiceRequestApiModel>(
            CpmCServiceRequestApiService.endpointEdit(undefined, serviceRequestUUID),
            payload
        );
    }
    public static deleteServiceRequest(serviceRequestUUID: string): Observable<boolean> {
        return CpmCApiBridge.httpDelete<boolean>(
            CpmCServiceRequestApiService.endpointDelete(undefined, serviceRequestUUID)
        );
    }
}
